import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ExamIntro from './ExamIntro';
import ExamResult from './ExamResult';

/**
 * Exam
 *
 * The Final Exam chatbot. Four sections, two swings per section.
 *
 * Flow:
 *   1. ExamIntro screen (gates on post-assessment, cooldown, already passed)
 *   2. Conversational chatbot: greeting, then 4 sections in order
 *      - Each section: main question, then optional follow-up if answer is thin
 *      - Follow-up triggered by lightweight client-side rubric check
 *   3. Submit all answers to scoreExam Cloud Function
 *   4. Result screen with pass/fail, section scores, feedback
 *
 * The Cloud Function does the heavy AI scoring using Claude. Client-side
 * we do a quick "is this answer substantive" check to decide whether to
 * fire a follow-up. That keeps the follow-up logic fast and predictable.
 */

const SECTIONS = [
  {
    id: 'rice',
    number: 1,
    title: 'Role-Specific RICE Prompt',
    mainQuestion: "Give me a role-specific RICE prompt you could use at work tomorrow morning. Not a sample, not a template, not something you saw in a workbook. A real one for a real task in your real job. All four RICE elements should be in there. Tell me what task it is for and what you expect to get back when you hit run.",
    followUpHint: "Hold up, I am not seeing the RICE pieces I need. Quick reminder, RICE here is the framework for writing AI prompts. R is Role (who AI should be), I is Intent (what output you want), C is Context (audience, stakes, background), E is Extras (format, tone, constraints). Take another swing and write a real prompt for a real task in your job, with all four elements clearly in there.",
    minAnswerLength: 150,
    requiredKeywords: { count: 2, lists: [['role'], ['intent', 'output', 'deliverable', 'goal'], ['context', 'audience', 'organization', 'company', 'team', 'stakes'], ['format', 'tone', 'length', 'extras', 'constraints', 'include', 'exclude']] },
  },
  {
    id: 'workflow',
    number: 2,
    title: 'Workflow Redesign',
    mainQuestion: "Walk me through one workflow you actually redesigned during this program. I want the before picture, the after picture, where AI is doing the work in the new version, and what could go wrong if a coworker tried to use your redesign without your context. Bonus points for honesty about the messy parts.",
    followUpHint: "Let me reset this one. I need a real workflow from your job. Pick one task you redesigned during this program. Tell me the steps you used to do (before), the steps you do now with AI (after), where exactly AI is doing the work in the new version (drafting, summarizing, checking, deciding, etc.), and what could go wrong if a coworker tried to use your redesign without your context. Take another swing.",
    minAnswerLength: 200,
    requiredKeywords: { count: 2, lists: [['before', 'used to', 'previously', 'manually'], ['after', 'now', 'new', 'redesigned'], ['ai', 'automate', 'augment', 'prompt', 'agent', 'chatgpt', 'claude'], ['risk', 'wrong', 'context', 'careful', 'limitation', 'fail', 'break']] },
  },
  {
    id: 'roi',
    number: 3,
    title: 'ROI Measurement',
    mainQuestion: "Show me the math. Pick one workflow or task where you can measure the impact. Give me the before number, the after number, and the annualized impact. Time saved, dollars saved, errors reduced, response time, whatever you measured. Tell me how you got the numbers and how confident you are in them. Rough math beats no math. Vibes are not numbers.",
    followUpHint: "I need real numbers, not feelings or questions back. Pick one workflow or task. Tell me how long it took BEFORE you used AI (in minutes or hours). Tell me how long it takes NOW with AI. Multiply the savings by how often you do it to get an annual number. Even rough math counts. If you do not have exact numbers, give me your best estimate and tell me how confident you are.",
    minAnswerLength: 120,
    requiredKeywords: { count: 1, lists: [['minute', 'hour', 'day', 'week', 'month', 'year', 'annual', '%', 'percent', 'dollar', '$', 'cost', 'rate', 'time']] },
    numbersRequired: true,
  },
  {
    id: 'agent',
    number: 4,
    title: 'Role-Specific Agent',
    mainQuestion: "Last one. Tell me about a role-specific agent you built or designed in this program. What does it do, who is it for, what kicks it off, what does it spit out, and how do you know it works. If it is running, tell me about the last time it ran. If it is specced but not live yet, tell me how you plan to test it. Either path is fine, I just want to see your thinking.",
    followUpHint: "I need more on this one. An agent is an AI assistant that runs on a trigger and produces an output. Tell me what your agent does, who it is for (you, your team, your clients), what kicks it off (an email, a request, a date), what it produces, and how you know it works. Make sure the answer is specific to your role, not something anyone could say.",
    minAnswerLength: 180,
    requiredKeywords: { count: 2, lists: [['trigger', 'when', 'fires', 'kicks off', 'starts'], ['output', 'returns', 'sends', 'produces', 'delivers', 'spits'], ['test', 'tested', 'run', 'ran', 'works', 'verified', 'checking'], ['role', 'job', 'specific', 'my work', 'function', 'department']] },
  },
];

/**
 * Lightweight client-side check to decide if a follow-up is needed.
 * Returns true if the answer is substantive enough to skip the follow-up.
 * False means we fire the follow-up.
 */
function answerIsSubstantive(answer, section) {
  const text = (answer || '').toLowerCase().trim();
  if (text.length < section.minAnswerLength) return false;

  // Check required keyword categories
  if (section.requiredKeywords) {
    let categoriesHit = 0;
    for (const keywordList of section.requiredKeywords.lists) {
      if (keywordList.some(kw => text.includes(kw.toLowerCase()))) {
        categoriesHit++;
      }
    }
    if (categoriesHit < section.requiredKeywords.count) return false;
  }

  // Numbers required for ROI section
  if (section.numbersRequired) {
    const hasNumbers = /\d+/.test(text);
    if (!hasNumbers) return false;
  }

  return true;
}

export default function Exam({ user, onNav }) {
  const [phase, setPhase] = useState('intro'); // 'intro' | 'exam' | 'scoring' | 'result'
  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState([]); // chat history
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [currentSwing, setCurrentSwing] = useState(1); // 1 = first answer, 2 = after follow-up
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [collectedAnswers, setCollectedAnswers] = useState({}); // {rice: {answer1, answer2}, ...}
  const [submitting, setSubmitting] = useState(false);
  const [examResult, setExamResult] = useState(null);
  const [scoringError, setScoringError] = useState(null);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Load profile once
  useEffect(() => {
    const load = async () => {
      const snap = await getDoc(doc(db, 'users', user.uid));
      if (snap.exists()) setProfile(snap.data());
    };
    load();
  }, [user.uid]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus textarea when it appears
  useEffect(() => {
    if (phase === 'exam' && !submitting) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [phase, currentSectionIdx, currentSwing, submitting]);

  const firstName = profile?.firstName || (user.displayName ? user.displayName.split(' ')[0] : '');

  // ========== START EXAM ==========
  const handleStart = () => {
    const openingBot = firstName
      ? `Hey ${firstName}. Look at you. Ten days, every workbook, both assessments, and now here you are at the final boss. Good news, it is just me. Bad news, it is also just me, so you cannot phone a friend. Four questions, no time limit, no points off for typos. Tell me what you built and how you think about it like you are explaining it to a smart coworker who skipped the program and now needs to catch up fast. Take a breath. Here we go.`
      : `Hey there. You made it. Four questions, no time limit, no points off for typos. Tell me what you built and how you think about it like you are explaining it to a smart coworker who skipped the program and now needs to catch up fast. Take a breath. Here we go.`;

    setMessages([
      { role: 'bot', text: openingBot, type: 'opening' },
      { role: 'bot', text: SECTIONS[0].mainQuestion, type: 'question', sectionId: SECTIONS[0].id },
    ]);
    setPhase('exam');
  };

  // ========== HANDLE ANSWER SUBMIT ==========
  const handleSubmitAnswer = () => {
    const trimmed = currentAnswer.trim();
    if (!trimmed) return;

    const section = SECTIONS[currentSectionIdx];

    // Push user answer to chat
    const newMessages = [...messages, { role: 'user', text: trimmed, swing: currentSwing }];

    // Store answer (build the updated answers object explicitly so we can pass it forward)
    const sectionAnswers = collectedAnswers[section.id] || {};
    const updatedAnswers = {
      ...collectedAnswers,
      [section.id]: currentSwing === 1
        ? { answer1: trimmed }
        : { ...sectionAnswers, answer2: trimmed },
    };
    setCollectedAnswers(updatedAnswers);

    setCurrentAnswer('');

    // Decide next step
    if (currentSwing === 1) {
      const substantive = answerIsSubstantive(trimmed, section);

      if (substantive) {
        moveToNextSectionOrFinish(newMessages, section, true, updatedAnswers);
      } else {
        newMessages.push({
          role: 'bot',
          text: section.followUpHint,
          type: 'followup',
          sectionId: section.id,
        });
        setMessages(newMessages);
        setCurrentSwing(2);
      }
    } else {
      // Second swing, score it and move on regardless
      const substantive = answerIsSubstantive(trimmed, section);
      moveToNextSectionOrFinish(newMessages, section, substantive, updatedAnswers);
    }
  };

  const moveToNextSectionOrFinish = (msgs, section, wasStrong, latestAnswers) => {
    const nextIdx = currentSectionIdx + 1;

    // Transition line
    let transition;
    if (currentSwing === 1 && wasStrong) {
      const strongOnes = [
        "There it is. That is the version you actually use on Monday.",
        "Locked in. That is a real answer.",
        "Good. That is exactly what I was after.",
        "Solid. Moving on.",
      ];
      transition = strongOnes[Math.floor(Math.random() * strongOnes.length)];
    } else if (currentSwing === 2 && wasStrong) {
      const followupGood = [
        "There it is. That is what I was looking for. Onto the next one.",
        "Better. That filled in the gap. Moving on.",
        "Good, that is the piece I needed. Next section.",
      ];
      transition = followupGood[Math.floor(Math.random() * followupGood.length)];
    } else {
      const followupWeak = [
        "Got it. I will work with what you gave me. Onto the next one.",
        "Alright, I have what I need to score this one. Moving on.",
        "Okay, taking what you gave me. Next up.",
      ];
      transition = followupWeak[Math.floor(Math.random() * followupWeak.length)];
    }

    if (nextIdx < SECTIONS.length) {
      const nextSection = SECTIONS[nextIdx];
      msgs.push({ role: 'bot', text: transition, type: 'transition' });
      msgs.push({ role: 'bot', text: nextSection.mainQuestion, type: 'question', sectionId: nextSection.id });
      setMessages(msgs);
      setCurrentSectionIdx(nextIdx);
      setCurrentSwing(1);
    } else {
      // Finish exam, pass the latest answers explicitly to avoid state race
      msgs.push({ role: 'bot', text: transition, type: 'transition' });
      msgs.push({
        role: 'bot',
        text: "Alright, that is all four. Give me a minute. I am going to look at everything you told me together, not just section by section, because how the four pieces connect matters. Hang tight.",
        type: 'closing',
      });
      setMessages(msgs);
      submitForScoring(latestAnswers);
    }
  };

  // ========== SUBMIT FOR SCORING ==========
  const submitForScoring = async (answersToScore) => {
    // Use passed-in answers if provided, otherwise fall back to state
    const finalAnswers = answersToScore || collectedAnswers;
    setPhase('scoring');
    setSubmitting(true);
    setScoringError(null);

    try {
      const functions = getFunctions(undefined, 'us-central1');
      const scoreExam = httpsCallable(functions, 'scoreExam');

      const result = await scoreExam({
        answers: finalAnswers,
        userProfile: {
          firstName: profile?.firstName || '',
          lastName: profile?.lastName || '',
          jobTitle: profile?.jobTitle || '',
          department: profile?.department || '',
          companyCode: profile?.companyCode || '',
        },
      });

      setExamResult(result.data);
      setPhase('result');
    } catch (err) {
      console.error('Exam scoring error:', err);
      setScoringError(err.message || 'Could not score your exam. Please try again.');
      setPhase('exam'); // Allow them to retry
    }
    setSubmitting(false);
  };

  // ========== RENDER ==========

  // Intro phase
  if (phase === 'intro') {
    return <ExamIntro user={user} onNav={onNav} onStart={handleStart} />;
  }

  // Result phase
  if (phase === 'result' && examResult) {
    return <ExamResult result={examResult} user={user} onNav={onNav} onRetake={() => {
      setPhase('intro');
      setMessages([]);
      setCurrentSectionIdx(0);
      setCurrentSwing(1);
      setCurrentAnswer('');
      setCollectedAnswers({});
      setExamResult(null);
    }} />;
  }

  // Scoring phase (waiting for AI)
  if (phase === 'scoring') {
    return (
      <div style={s.wrapper}>
        <div style={s.header}>
          <div style={s.dayTag}>Final Exam · Scoring</div>
          <div style={s.title}>Reading your answers</div>
        </div>
        <div style={s.scoringBody}>
          <div style={s.spinner}>
            <div style={s.spinnerDot}></div>
            <div style={s.spinnerDot}></div>
            <div style={s.spinnerDot}></div>
            <div style={s.spinnerDot}></div>
          </div>
          <div style={s.scoringText}>
            Looking at everything you wrote, section by section and as a whole. This takes about a minute.
          </div>
          <div style={s.scoringSubtext}>
            Do not refresh or close this tab. We will be right with you.
          </div>
        </div>
      </div>
    );
  }

  // Exam phase
  const currentSection = SECTIONS[currentSectionIdx];

  return (
    <div style={s.wrapper}>
      <div style={s.header}>
        <div style={s.dayTagRow}>
          <span style={s.dayTag}>Final Exam · Section {currentSection.number} of 4</span>
          <span style={s.swingTag}>{currentSwing === 1 ? 'First answer' : 'Follow-up'}</span>
        </div>
        <div style={s.titleSmall}>{currentSection.title}</div>
      </div>

      <div style={s.progressBar}>
        {SECTIONS.map((sec, idx) => (
          <div key={sec.id} style={{
            ...s.progressSeg,
            background: idx < currentSectionIdx ? '#1a8a4a' : idx === currentSectionIdx ? '#D4A847' : '#E8E6E2',
          }}></div>
        ))}
      </div>

      <div style={s.chatArea}>
        {messages.map((msg, i) => (
          <div key={i} style={msg.role === 'bot' ? s.botRow : s.userRow}>
            {msg.role === 'bot' && (
              <div style={s.botAvatar}>
                <div style={s.botAvatarInner}>TCF</div>
              </div>
            )}
            <div style={msg.role === 'bot' ? s.botBubble : s.userBubble}>
              {msg.text}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef}></div>
      </div>

      {scoringError && (
        <div style={s.errorBox}>
          <strong>Something went wrong.</strong> {scoringError}
          <button style={s.retryBtn} onClick={() => submitForScoring(collectedAnswers)}>Try scoring again</button>
        </div>
      )}

      <div style={s.inputArea}>
        <textarea
          ref={textareaRef}
          style={s.textarea}
          value={currentAnswer}
          onChange={e => setCurrentAnswer(e.target.value)}
          placeholder={currentSwing === 1 ? "Type your answer here. Take your time." : "Take another swing. Fill in what was missing."}
          rows={6}
          disabled={submitting}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSubmitAnswer();
            }
          }}
        />
        <div style={s.inputFooter}>
          <span style={s.charCount}>{currentAnswer.length} characters · Ctrl+Enter to submit</span>
          <button
            style={{ ...s.submitBtn, opacity: currentAnswer.trim() ? 1 : 0.4, cursor: currentAnswer.trim() ? 'pointer' : 'not-allowed' }}
            onClick={handleSubmitAnswer}
            disabled={!currentAnswer.trim() || submitting}
          >
            Submit Answer →
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '880px', paddingBottom: '60px', fontFamily: 'DM Sans, sans-serif' },

  header: { background: '#1B2A4A', padding: '20px 28px' },
  dayTagRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847' },
  swingTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', background: 'rgba(212,168,71,0.15)', padding: '4px 10px' },
  title: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff' },
  titleSmall: { fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#fff' },

  progressBar: { display: 'flex', height: '4px', background: '#E8E6E2' },
  progressSeg: { flex: 1, transition: 'background 0.3s' },

  chatArea: { background: '#FAF8F5', border: '1.5px solid #E8E6E2', borderTop: 'none', borderBottom: 'none', padding: '24px', maxHeight: '500px', overflowY: 'auto' },

  botRow: { display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' },
  userRow: { display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' },

  botAvatar: { width: '36px', height: '36px', background: '#1B2A4A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  botAvatarInner: { fontFamily: 'Playfair Display, serif', fontSize: '10px', fontWeight: '700', color: '#D4A847', letterSpacing: '0.5px' },

  botBubble: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '14px 18px', fontSize: '14px', color: '#3D3D3D', lineHeight: '1.6', maxWidth: '85%', fontWeight: '400' },
  userBubble: { background: '#1B2A4A', color: '#FFFFFF', padding: '14px 18px', fontSize: '14px', lineHeight: '1.6', maxWidth: '85%', fontWeight: '300', whiteSpace: 'pre-wrap' },

  inputArea: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '12px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', boxSizing: 'border-box', resize: 'vertical', minHeight: '120px', lineHeight: '1.6' },
  inputFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' },
  charCount: { fontSize: '11px', color: '#8A8A8A', fontStyle: 'italic' },
  submitBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '12px 24px', fontFamily: 'Playfair Display, serif', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },

  errorBox: { background: '#fdecea', border: '1.5px solid #C0392B', borderTop: 'none', padding: '14px 20px', fontSize: '13px', color: '#a32d2d', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' },
  retryBtn: { background: '#C0392B', color: '#fff', border: 'none', padding: '8px 16px', fontFamily: 'Playfair Display, serif', fontSize: '12px', fontWeight: '700', cursor: 'pointer', marginLeft: 'auto' },

  scoringBody: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '60px 40px', textAlign: 'center' },
  spinner: { display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '32px' },
  spinnerDot: { width: '12px', height: '12px', borderRadius: '50%', background: '#D4A847', animation: 'spinnerPulse 1.2s ease-in-out infinite' },
  scoringText: { fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1B2A4A', marginBottom: '12px', lineHeight: '1.5' },
  scoringSubtext: { fontSize: '13px', color: '#8A8A8A', fontStyle: 'italic' },
};

// Inject spinner animation
if (typeof document !== 'undefined' && !document.getElementById('exam-spinner-anim')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'exam-spinner-anim';
  styleEl.textContent = `
    @keyframes spinnerPulse {
      0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
      40% { opacity: 1; transform: scale(1.2); }
    }
    [style*="spinnerPulse"]:nth-child(2) { animation-delay: 0.15s !important; }
    [style*="spinnerPulse"]:nth-child(3) { animation-delay: 0.3s !important; }
    [style*="spinnerPulse"]:nth-child(4) { animation-delay: 0.45s !important; }
  `;
  document.head.appendChild(styleEl);
}
